import { useAuth } from '../context/AuthContext'
import { Card, CardContent } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Flame, Trophy, BarChart2, Users, Settings, LogOut, Star, Medal, Sparkles, Calendar } from 'lucide-react'
import { clsx } from 'clsx'
import { format } from 'date-fns'
import { useState, useEffect } from 'react'

export default function Profile() {
  const { profile, user, updateProfile, signOut } = useAuth()
  const [activeTab, setActiveTab] = useState<'overview' | 'stats' | 'streaks' | 'duels' | 'settings'>('overview')
  const [editing, setEditing] = useState(false)
  const [displayName, setDisplayName] = useState(profile?.display_name || '')
  const [username, setUsername] = useState(profile?.username || '')
  const [saving, setSaving] = useState(false)
  const [realStats, setRealStats] = useState<Stats | null>(null)

  useEffect(() => {
    if (user) {
      fetchRealStats()
    }
  }, [user])

  const fetchRealStats = async () => {
    try {
      // In a real app, this would fetch from Supabase
      // For now, we'll use the profile data and add some computed stats
      if (profile) {
        setRealStats({
          totalGames: profile.total_plays || 0,
          currentStreak: profile.current_streak || 0,
          longestStreak: profile.longest_streak || 0,
          avgScore: 72, // Would compute from daily_responses
          duelsWon: 3,
          duelsPlayed: 5,
          promptsSubmitted: 1,
          promptsUsed: 0,
        })
      }
    } catch (err) {
      console.error('Error fetching stats:', err)
    }
  }

  const stats = realStats || {
    totalGames: profile?.total_plays || 0,
    currentStreak: profile?.current_streak || 0,
    longestStreak: profile?.longest_streak || 0,
    avgScore: 72,
    duelsWon: 3,
    duelsPlayed: 5,
    promptsSubmitted: 1,
    promptsUsed: 0,
  }

  const handleSaveProfile = async () => {
    if (!user) return
    setSaving(true)
    try {
      const { error } = await updateProfile({ display_name: displayName, username })
      if (error) throw error
      setEditing(false)
    } catch (err) {
      console.error(err)
    } finally {
      setSaving(false)
    }
  }

  const handleSignOut = async () => {
    await signOut()
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 lg:py-10 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-surface-900 dark:text-surface-100">Profile</h1>
          <p className="text-surface-500 dark:text-surface-400">Your Overlap journey</p>
        </div>
        <Button variant="ghost" size="sm" onClick={() => setActiveTab('settings')}>
          <Settings className="w-4 h-4 mr-2" />
          Settings
        </Button>
      </div>

      {/* Profile Header Card */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
            <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-brand-500 to-brand-600 flex items-center justify-center text-white font-black text-4xl flex-shrink-0">
              {profile?.display_name?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              {editing ? (
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1">Display Name</label>
                    <input
                      type="text"
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                      className="w-full input-field"
                      maxLength={20}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1">Username</label>
                    <div className="flex items-center gap-2">
                      <span className="text-surface-500">@</span>
                      <input
                        type="text"
                        value={username}
                        onChange={(e) => setUsername(e.target.value.replace(/[^a-zA-Z0-9_]/g, ''))}
                        className="flex-1 input-field"
                        maxLength={15}
                      />
                    </div>
                    <p className="text-xs text-surface-500 mt-1">Letters, numbers, underscores only</p>
                  </div>
                  <div className="flex gap-3 pt-2">
                    <Button onClick={handleSaveProfile} loading={saving} className="flex-1">
                      Save
                    </Button>
                    <Button variant="ghost" onClick={() => { setEditing(false); setDisplayName(profile?.display_name || ''); setUsername(profile?.username || '') }} className="flex-1">
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-3 mb-2">
                    <h2 className="text-2xl font-bold text-surface-900 dark:text-surface-100">
                      {profile?.display_name || 'Player'}
                    </h2>
                    {profile?.username && (
                      <span className="text-surface-500 dark:text-surface-400">@{profile.username}</span>
                    )}
                  </div>
                  <p className="text-surface-500 dark:text-surface-400 mb-4">
                    Member since {profile ? format(new Date(profile.created_at), 'MMMM yyyy') : 'recently'}
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <StatBadge icon={Flame} value={stats.currentStreak} label="Day Streak" color="orange" />
                    <StatBadge icon={Trophy} value={stats.longestStreak} label="Best Streak" color="yellow" />
                    <StatBadge icon={BarChart2} value={stats.totalGames} label="Games Played" color="blue" />
                    <StatBadge icon={Medal} value={stats.avgScore} label="Avg Score" color="purple" suffix="%" />
                  </div>
                  {!editing && (
                    <Button variant="ghost" size="sm" onClick={() => setEditing(true)} className="mt-4">
                      <Sparkles className="w-4 h-4 mr-2" />
                      Edit Profile
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tab Navigation */}
      <div className="flex gap-1 bg-surface-100 dark:bg-surface-800 rounded-xl p-1">
        {([
          { id: 'overview', label: 'Overview', icon: BarChart2 },
          { id: 'stats', label: 'Stats', icon: Trophy },
          { id: 'streaks', label: 'Streaks', icon: Flame },
          { id: 'duels', label: 'Duels', icon: Users },
        ] as const).map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={clsx(
              'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all',
              activeTab === tab.id
                ? 'bg-white dark:bg-surface-900 text-brand-600 dark:text-brand-400 shadow-sm'
                : 'text-surface-500 hover:text-surface-700 dark:text-surface-400 dark:hover:text-surface-200'
            )}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && <OverviewTab stats={stats} />}
      {activeTab === 'stats' && <StatsTab stats={stats} />}
      {activeTab === 'streaks' && <StreaksTab profile={profile} />}
      {activeTab === 'duels' && <DuelsTab />}
      {activeTab === 'settings' && <SettingsTab onSignOut={handleSignOut} />}
    </div>
  )
}

function StatBadge({ icon: Icon, value, label, color, suffix = '' }: { icon: React.ComponentType<{ className?: string }>, value: string | number, label: string, color: 'orange' | 'yellow' | 'blue' | 'purple' | 'pink' | 'brand', suffix?: string }) {
  const colors = {
    orange: 'bg-orange-50 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
    yellow: 'bg-yellow-50 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    blue: 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    purple: 'bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
    pink: 'bg-pink-50 text-pink-700 dark:bg-pink-900/30 dark:text-pink-400',
    brand: 'bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-400',
  }

  return (
    <div className={clsx('flex items-center gap-2 px-3 py-2 rounded-xl text-sm', colors[color])}>
      <Icon className="w-4 h-4" />
      <span className="font-bold">{value}{suffix}</span>
      <span className="text-xs opacity-80">{label}</span>
    </div>
  )
}

interface Stats {
  totalGames: number
  currentStreak: number
  longestStreak: number
  avgScore: number
  duelsWon: number
  duelsPlayed: number
  promptsSubmitted: number
  promptsUsed: number
}

interface OverviewTabProps {
  stats: Stats
}

function OverviewTab({ stats }: OverviewTabProps) {
  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="pt-6">
          <h3 className="font-semibold text-surface-900 dark:text-surface-100 mb-4">Quick Stats</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard icon={Flame} value={stats.currentStreak} label="Current Streak" trend="+2 this week" color="orange" />
            <StatCard icon={Trophy} value={stats.totalGames} label="Total Games" trend="+5 this month" color="blue" />
            <StatCard icon={Star} value={stats.avgScore} label="Avg Overlap" suffix="%" trend="+3 vs last month" color="purple" />
            <StatCard icon={Medal} value={stats.duelsWon} label="Duels Won" trend={`${stats.duelsPlayed} played`} color="yellow" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6">
          <h3 className="font-semibold text-surface-900 dark:text-surface-100 mb-4">Recent Activity</h3>
          <div className="space-y-3">
            <ActivityItem 
              icon={Calendar} 
              color="brand" 
              title="Daily Overlap" 
              desc='Scored 84 on "Pineapple on Pizza"' 
              time="2 hours ago" 
              score={84}
            />
            <ActivityItem 
              icon={Users} 
              color="purple" 
              title="Duel vs @sarah" 
              desc="78% sync — Soulmates!" 
              time="Yesterday" 
              score={78}
            />
            <ActivityItem 
              icon={Flame} 
              color="orange" 
              title="Streak Milestone" 
              desc="Reached 7 days! Earned a freeze." 
              time="3 days ago" 
            />
            <ActivityItem 
              icon={Trophy} 
              color="yellow" 
              title="Daily Overlap" 
              desc='Scored 91 on "Hot Dog = Sandwich"' 
              time="5 days ago" 
              score={91}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

interface StatsTabProps {
  stats: {
    totalGames: number
    currentStreak: number
    longestStreak: number
    avgScore: number
    duelsWon: number
    duelsPlayed: number
    promptsSubmitted: number
    promptsUsed: number
  }
}

function StatsTab({ stats }: StatsTabProps) {
  const statItems = [
    { icon: Trophy, label: 'Total Games', value: stats.totalGames, color: 'blue' },
    { icon: Flame, label: 'Current Streak', value: stats.currentStreak, color: 'orange' },
    { icon: Trophy, label: 'Longest Streak', value: stats.longestStreak, color: 'yellow' },
    { icon: Star, label: 'Average Score', value: `${stats.avgScore}%`, color: 'purple' },
    { icon: Users, label: 'Duels Played', value: stats.duelsPlayed, color: 'purple' },
    { icon: Medal, label: 'Duels Won', value: stats.duelsWon, color: 'yellow' },
    { icon: Sparkles, label: 'Prompts Submitted', value: stats.promptsSubmitted, color: 'pink' },
    { icon: Star, label: 'Prompts Used', value: stats.promptsUsed, color: 'purple' },
  ]

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {statItems.map((item, i) => (
        <Card key={i} className="text-center">
          <CardContent className="py-6">
            <div className={clsx('w-12 h-12 mx-auto mb-3 rounded-xl flex items-center justify-center', 
              item.color === 'blue' && 'bg-blue-500',
              item.color === 'orange' && 'bg-orange-500',
              item.color === 'yellow' && 'bg-yellow-500',
              item.color === 'purple' && 'bg-purple-500',
              item.color === 'pink' && 'bg-pink-500',
            )}>
              <item.icon className="w-6 h-6 text-white" />
            </div>
            <p className="text-3xl font-black text-surface-900 dark:text-surface-100">{item.value}</p>
            <p className="text-sm text-surface-500 dark:text-surface-400 mt-1">{item.label}</p>
          </CardContent>
        </Card>
      ))}
    </div>
  )
}

function StreaksTab({ profile }: { profile: { current_streak?: number } | null }) {
  const [history, setHistory] = useState<Array<{date: string, played: boolean, day: string}>>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // In a real app, fetch from Supabase daily_responses
    // For now, generate from profile streak
    const streak = profile?.current_streak || 0
    const hist = Array.from({ length: 28 }, (_, i) => {
      const date = new Date()
      date.setDate(date.getDate() - (27 - i))
      const played = i >= 28 - streak
      return { date: date.toISOString().split('T')[0], played, day: date.toLocaleDateString('en-US', { weekday: 'short' }) }
    })
    setHistory(hist)
    setLoading(false)
  }, [profile?.current_streak])

  const currentStreak = profile?.current_streak || 0

  if (loading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-center py-8">
            <div className="w-8 h-8 border-4 border-brand-500 border-t-transparent rounded-full animate-spin" />
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-semibold text-surface-900 dark:text-surface-100">Streak Calendar</h3>
            <div className="flex items-center gap-2 text-sm text-surface-500">
              <Flame className="w-4 h-4 text-orange-500" />
              <span>{currentStreak} day streak</span>
            </div>
          </div>
          <div className="grid grid-cols-7 gap-1">
            {history.map((day, i) => (
              <div key={i} className="aspect-square rounded-lg flex flex-col items-center justify-center text-xs">
                <span className="text-surface-500">{day.day}</span>
                <div className={clsx(
                  'w-8 h-8 rounded-full flex items-center justify-center mt-1 transition-all',
                  day.played 
                    ? 'bg-gradient-to-br from-orange-500 to-orange-600 text-white font-bold' 
                    : 'bg-surface-200 dark:bg-surface-700 text-surface-400'
                )}>
                  {new Date(day.date).getDate()}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6">
          <h3 className="font-semibold text-surface-900 dark:text-surface-100 mb-4">Streak Milestones</h3>
          <div className="space-y-3">
            <Milestone days={3} label="Getting Started" icon={Sparkles} reached={currentStreak >= 3} />
            <Milestone days={7} label="Week Warrior" icon={Flame} reached={currentStreak >= 7} reward="Freeze earned!" />
            <Milestone days={14} label="Fortnight Fighter" icon={Sparkles} reached={currentStreak >= 14} reward="Freeze earned!" />
            <Milestone days={30} label="Monthly Master" icon={Trophy} reached={currentStreak >= 30} reward="Exclusive badge!" />
            <Milestone days={50} label="Half Century" icon={Medal} reached={currentStreak >= 50} reward="Legendary status!" />
            <Milestone days={100} label="Centurion" icon={Star} reached={currentStreak >= 100} reward="Hall of Fame!" />
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

function DuelsTab() {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="text-center py-12">
          <Users className="w-16 h-16 mx-auto mb-4 text-surface-300 dark:text-surface-600" />
          <h3 className="text-lg font-semibold text-surface-900 dark:text-surface-100 mb-2">No Duels Yet</h3>
          <p className="text-surface-500 dark:text-surface-400 mb-6">Challenge a friend to see your sync percentage!</p>
          <Button onClick={() => window.location.href = '/'}>
            <Users className="w-4 h-4 mr-2" />
            Create a Duel
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

interface SettingsTabProps {
  onSignOut: () => void
}

function SettingsTab({ onSignOut }: SettingsTabProps) {
  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="pt-6">
          <h3 className="font-semibold text-surface-900 dark:text-surface-100 mb-4">Account</h3>
          <div className="space-y-3">
            <SettingRow 
              icon={LogOut} 
              title="Sign Out" 
              desc="Sign out of your account on this device" 
              action={<Button variant="ghost" size="sm" onClick={onSignOut}>Sign Out</Button>} 
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6">
          <h3 className="font-semibold text-surface-900 dark:text-surface-100 mb-4">About</h3>
          <div className="space-y-3 text-sm text-surface-500">
            <p>Overlap v1.0.0</p>
            <p>A daily game about how in sync you are with everyone else.</p>
            <p className="pt-2 border-t border-surface-200 dark:border-surface-800">Made with ❤️ using React, TypeScript, and Supabase</p>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

interface SettingRowProps {
  icon: React.ElementType
  title: string
  desc: string
  action: React.ReactNode
}

function SettingRow({ icon: Icon, title, desc, action }: SettingRowProps) {
  return (
    <div className="flex items-center justify-between p-3 bg-surface-50 dark:bg-surface-900/50 rounded-xl">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-surface-100 dark:bg-surface-800 flex items-center justify-center">
          <Icon className="w-5 h-5 text-surface-600 dark:text-surface-400" />
        </div>
        <div>
          <p className="font-medium text-surface-900 dark:text-surface-100">{title}</p>
          <p className="text-sm text-surface-500">{desc}</p>
        </div>
      </div>
      {action}
    </div>
  )
}

function StatCard({ icon: Icon, value, label, trend, color, suffix = '' }: { icon: React.ComponentType<{ className?: string }>, value: string | number, label: string, trend?: string, color: 'blue' | 'orange' | 'yellow' | 'purple' | 'pink', suffix?: string }) {
  const colors = {
    blue: 'bg-blue-500',
    orange: 'bg-orange-500',
    yellow: 'bg-yellow-500',
    purple: 'bg-purple-500',
    pink: 'bg-pink-500',
  }

  return (
    <div className="text-center p-4 bg-surface-50 dark:bg-surface-900/50 rounded-xl">
      <div className={clsx('w-10 h-10 mx-auto mb-2 rounded-xl flex items-center justify-center', colors[color])}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <p className="text-2xl font-black text-surface-900 dark:text-surface-100">{value}{suffix}</p>
      <p className="text-sm text-surface-500 dark:text-surface-400 mt-1">{label}</p>
      {trend && <p className="text-xs text-green-600 dark:text-green-400 mt-1">{trend}</p>}
    </div>
  )
}

function ActivityItem({ icon: Icon, color, title, desc, time, score }: { icon: React.ComponentType<{ className?: string }>, color: 'brand' | 'purple' | 'orange' | 'yellow', title: string, desc: string, time: string, score?: number }) {
  const colors = {
    brand: 'bg-brand-500',
    purple: 'bg-purple-500',
    orange: 'bg-orange-500',
    yellow: 'bg-yellow-500',
  }

  return (
    <div className="flex items-center gap-4 p-3 bg-surface-50 dark:bg-surface-900/50 rounded-xl">
      <div className={clsx('w-10 h-10 rounded-xl flex items-center justify-center', colors[color])}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-surface-900 dark:text-surface-100">{title}</p>
        <p className="text-sm text-surface-500">{desc}</p>
      </div>
      <div className="text-right">
        {score !== undefined && (
          <p className="font-bold text-brand-600 dark:text-brand-400">{score}</p>
        )}
        <p className="text-xs text-surface-400">{time}</p>
      </div>
    </div>
  )
}

function Milestone({ days, label, icon: Icon, reached, reward }: { days: number, label: string, icon: React.ComponentType<{ className?: string }>, reached: boolean, reward?: string }) {
  return (
    <div className="flex items-center gap-4 p-3 bg-surface-50 dark:bg-surface-900/50 rounded-xl">
      <div className={clsx('w-10 h-10 rounded-xl flex items-center justify-center', 
        reached ? 'bg-gradient-to-br from-yellow-500 to-orange-500' : 'bg-surface-200 dark:bg-surface-700'
      )}>
        <Icon className={clsx('w-5 h-5', reached ? 'text-white' : 'text-surface-400')} />
      </div>
      <div className="flex-1">
        <p className={clsx('font-medium', reached ? 'text-surface-900 dark:text-surface-100' : 'text-surface-500')}>
          {label} ({days} days)
        </p>
        {reward && <p className="text-sm text-green-600 dark:text-green-400">{reward}</p>}
      </div>
      {reached && <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>}
    </div>
  )
}